import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect, useSelector } from "react-redux";
import _pick from "lodash/pick";
import _get from "lodash/get";
import useActiveEditing from "../../../../hooks/useActiveEditing";

import EditableTextDetailRow from "./EditableTextDetailRow";
import FormikForm from "../../../../forms/FormikForm";
import { createDeepEqualSelector } from "../../../../../utils/selectors";
import {
  isPendingRequest,
  isFailedRequest,
  isSuccessfulRequest,
} from "../../../../../utils/dataComponentUtils";
import { getDataComponent } from "../../../../../reducers/dataComponentReducer";
import {
  performUpdateRequest,
  cloneDataComponentOverwriteProp,
} from "../../../../../actions/dataComponentActions";
import * as REQUEST_TYPES from "../../../../../constants/RequestTypes";
import { getResourcesV2 } from "../../../../../utils/bluechipUtils";

const updateOriginalRow = (
  rows,
  itemId,
  values,
  idPath,
  valuePath,
  setRows
) => {
  if (!rows) return;
  if (!setRows) {
    const row = rows.find(row => row[idPath] === itemId);
    row[valuePath] = values[valuePath];
    return;
  }

  setRows(
    rows.map(row => ({
      ...row,
      ...(row[idPath] === itemId ? { [valuePath]: values[valuePath] } : {}),
    }))
  );
};

const useUpdateOriginalRow = ({
  dataComponentId,
  rows,
  itemId,
  props,
  setRows,
}) => {
  const valuesRef = useRef({});
  const isEditable = useSelector(state =>
    _get(state.editableTextDetailRow, `${dataComponentId}.isEditing`)
  );
  const prevIsEditable = useRef(isEditable);

  useEffect(() => {
    if (!isEditable && prevIsEditable.current !== isEditable) {
      updateOriginalRow(
        rows,
        itemId,
        valuesRef.current,
        props.idPath,
        props.path,
        setRows
      );
    }

    prevIsEditable.current = isEditable;
  }, [isEditable, itemId, props.idPath, props.path, rows, setRows]);

  return valuesRef;
};

export const EditableTextDetailRowContainer = ({
  initialValues,
  row,
  validationSchema,
  dataComponentId: srcDataComponentId,
  performUpdateRequest,
  cloneDataComponentOverwriteProp,
  itemId,
  textInputProps,
  isReadOnly: isReadOnlyFn,
  rows,
  setRows,
  dstDataComponentProps,
  ...props
}) => {
  const dataComponentId = `${srcDataComponentId}-${itemId}`;

  const isEditing = useSelector(state =>
    _get(state.editableTextDetailRow, `${dataComponentId}.isEditing`)
  );
  const isAnyEditing = useSelector(state => state.activeEditing > 0);

  useActiveEditing(!!isEditing);

  const valuesRef = useUpdateOriginalRow({
    dataComponentId,
    rows,
    itemId,
    props,
    setRows,
  });

  useEffect(() => {
    if (itemId) {
      cloneDataComponentOverwriteProp(
        srcDataComponentId,
        dataComponentId,
        true,
        false,
        dstDataComponentProps
      );
    }
  }, [
    cloneDataComponentOverwriteProp,
    srcDataComponentId,
    dataComponentId,
    itemId,
    dstDataComponentProps,
  ]);

  const handleSubmit = useCallback(
    (values, { isSubmitting }) => {
      if (isSubmitting) return;

      performUpdateRequest(dataComponentId, itemId, values);
    },
    [itemId, performUpdateRequest, dataComponentId]
  );

  const isReadOnly =
    typeof isReadOnlyFn === "function"
      ? isReadOnlyFn(itemId, row, dataComponentId)
      : isReadOnlyFn;

  return (
    <FormikForm
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      autoSave
      debounce={3000}
      enableReinitialize
      deferred={isAnyEditing}
    >
      {({ values }) => {
        valuesRef.current = { ...values };

        return (
          <EditableTextDetailRow
            {...props}
            {...textInputProps}
            isReadOnly={isReadOnly}
            value={values[props.path]}
            dataComponentId={dataComponentId}
          />
        );
      }}
    </FormikForm>
  );
};

EditableTextDetailRowContainer.propTypes = {
  row: PropTypes.shape({}),
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  setRows: PropTypes.func,
  initialValues: PropTypes.shape({}),
  textInputProps: PropTypes.shape({ helperText: PropTypes.string }),
  colSpan: PropTypes.shape({ prev: PropTypes.number, pos: PropTypes.number }),
  performUpdateRequest: PropTypes.func.isRequired,
  cloneDataComponentOverwriteProp: PropTypes.func.isRequired,
  validationSchema: PropTypes.shape({ valid: PropTypes.func }),
  dataComponentId: PropTypes.string,
  path: PropTypes.string.isRequired,
  itemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  status: PropTypes.string,
  editable: PropTypes.bool,
  dstDataComponentProps: PropTypes.object,
};

EditableTextDetailRow.defaultProps = {
  textInputProps: {},
  dstDataComponentProps: {},
};

export const mapStateToProps = () => {
  const initialValuesSelector = createDeepEqualSelector(
    (_, { row }) => row,
    (_, { path }) => path,
    (row, path) => _pick(row, [path, "id"])
  );
  return (state, { row, rows: initialRows, idPath, path, dataComponentId }) => {
    const itemId = _get(row, idPath);

    const dataComponent = getDataComponent(
      `${dataComponentId}-${itemId}`,
      state
    );

    const rows =
      initialRows ??
      getResourcesV2(
        getDataComponent(dataComponentId, state),
        state,
        REQUEST_TYPES.LIST
      );

    let status = null;
    if (isPendingRequest(dataComponent, REQUEST_TYPES.UPDATE)) {
      status = "pending";
    }
    if (isSuccessfulRequest(dataComponent, REQUEST_TYPES.UPDATE)) {
      status = "success";
    }
    if (isFailedRequest(dataComponent, REQUEST_TYPES.UPDATE)) {
      status = "fail";
    }

    return {
      initialValues: initialValuesSelector(null, { row, path }),
      itemId,
      status,
      rows,
    };
  };
};

const mapDispatchToProps = {
  performUpdateRequest,
  cloneDataComponentOverwriteProp,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditableTextDetailRowContainer);
